/**
 * Transport shared by the web client and the Tauri desktop shell.
 *
 * Handles bearer auth, single-flight refresh-token rotation (the backend
 * invalidates the whole family if a token is replayed, so concurrent 401s must
 * not each trigger their own refresh) and typed error envelopes.
 */

export interface TokenPair {
  access_token: string;
  refresh_token: string;
  expires_in: number;
}

export interface TokenStore {
  read(): TokenPair | null;
  write(tokens: TokenPair | null): void;
}

export interface ApiErrorBody {
  error: { code: string; message: string; details?: Record<string, unknown> };
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown>;

  constructor(status: number, body: ApiErrorBody | null, fallback = 'Request failed') {
    super(body?.error?.message ?? fallback);
    this.name = 'ApiError';
    this.status = status;
    this.code = body?.error?.code ?? 'unknown_error';
    this.details = body?.error?.details ?? {};
  }

  /** The tenant's plan does not include the requested module. */
  get isFeatureLocked(): boolean {
    return this.code === 'feature_locked';
  }

  get isOffline(): boolean {
    return this.status === 0;
  }
}

export interface ApiClientOptions {
  baseUrl: string;
  tokens: TokenStore;
  platform?: 'web' | 'desktop' | 'mobile';
  onUnauthorized?: () => void;
}

type QueryValue = string | number | boolean | null | undefined | Array<string | number>;

export class ApiClient {
  private refreshInFlight: Promise<TokenPair | null> | null = null;

  constructor(private readonly options: ApiClientOptions) {}

  get baseUrl(): string {
    return this.options.baseUrl;
  }

  get accessToken(): string | null {
    return this.options.tokens.read()?.access_token ?? null;
  }

  setTokens(tokens: TokenPair | null): void {
    this.options.tokens.write(tokens);
  }

  /**
   * Absolute URL for an API path.
   *
   * `baseUrl` is `/api/v1` when the web app is served next to the API, and
   * `new URL('/api/v1/x')` throws "Invalid URL" without a base to resolve it
   * against. Desktop and mobile use an absolute base, which ignores the second
   * argument, so one expression serves all of them.
   */
  private urlFor(path: string): URL {
    const base = typeof location === 'undefined' ? undefined : location.href;
    return new URL(this.options.baseUrl.replace(/\/$/, '') + path, base);
  }

  async request<T>(
    method: string,
    path: string,
    init: { body?: unknown; query?: Record<string, QueryValue>; retry?: boolean } = {},
  ): Promise<T> {
    const url = this.urlFor(path);
    for (const [key, value] of Object.entries(init.query ?? {})) {
      if (value === undefined || value === null || value === '') continue;
      if (Array.isArray(value)) value.forEach((v) => url.searchParams.append(key, String(v)));
      else url.searchParams.set(key, String(value));
    }

    const headers: Record<string, string> = {
      Accept: 'application/json',
      'X-Client-Platform': this.options.platform ?? 'web',
    };
    const token = this.accessToken;
    if (token) headers.Authorization = `Bearer ${token}`;

    let payload: BodyInit | undefined;
    if (init.body instanceof FormData) {
      payload = init.body;
    } else if (init.body !== undefined) {
      headers['Content-Type'] = 'application/json';
      payload = JSON.stringify(init.body);
    }

    let response: Response;
    try {
      response = await fetch(url.toString(), { method, headers, body: payload });
    } catch (cause) {
      // Network failure — the offline queue in the client decides what to do.
      throw new ApiError(0, null, cause instanceof Error ? cause.message : 'Network error');
    }

    if (response.status === 401 && init.retry !== false) {
      const refreshed = await this.refresh();
      if (refreshed) {
        return this.request<T>(method, path, { ...init, retry: false });
      }
      this.options.onUnauthorized?.();
    }

    if (response.status === 204) return undefined as T;

    const text = await response.text();
    const data = text ? JSON.parse(text) : null;
    if (!response.ok) throw new ApiError(response.status, data);
    return data as T;
  }

  get<T>(path: string, query?: Record<string, QueryValue>): Promise<T> {
    return this.request<T>('GET', path, { query });
  }

  post<T>(path: string, body?: unknown, query?: Record<string, QueryValue>): Promise<T> {
    return this.request<T>('POST', path, { body, query });
  }

  patch<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('PATCH', path, { body });
  }

  put<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('PUT', path, { body });
  }

  delete<T>(path: string): Promise<T> {
    return this.request<T>('DELETE', path, {});
  }

  /**
   * Fetch a non-JSON body (CSV exports, generated documents).
   *
   * Cannot go through `request`, which parses every response as JSON — a CSV
   * would throw on the first semicolon. Auth still applies, so the browser
   * cannot simply point an `<a download>` at the URL.
   */
  async download(path: string, query?: Record<string, QueryValue>): Promise<Blob> {
    const url = this.urlFor(path);
    for (const [key, value] of Object.entries(query ?? {})) {
      if (value === undefined || value === null || value === '') continue;
      if (Array.isArray(value)) value.forEach((v) => url.searchParams.append(key, String(v)));
      else url.searchParams.set(key, String(value));
    }

    const headers: Record<string, string> = {
      'X-Client-Platform': this.options.platform ?? 'web',
    };
    const token = this.accessToken;
    if (token) headers.Authorization = `Bearer ${token}`;

    let response: Response;
    try {
      response = await fetch(url.toString(), { method: 'GET', headers });
    } catch (cause) {
      throw new ApiError(0, null, cause instanceof Error ? cause.message : 'Network error');
    }

    if (!response.ok) {
      const text = await response.text();
      throw new ApiError(response.status, text ? JSON.parse(text) : null);
    }
    return response.blob();
  }

  /** Rotate the refresh token, coalescing concurrent callers into one call. */
  private async refresh(): Promise<TokenPair | null> {
    if (this.refreshInFlight) return this.refreshInFlight;
    const current = this.options.tokens.read();
    if (!current?.refresh_token) return null;

    this.refreshInFlight = (async () => {
      try {
        const response = await fetch(`${this.options.baseUrl}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: current.refresh_token }),
        });
        if (!response.ok) {
          this.options.tokens.write(null);
          return null;
        }
        const tokens = (await response.json()) as TokenPair;
        this.options.tokens.write(tokens);
        return tokens;
      } catch {
        return null;
      } finally {
        this.refreshInFlight = null;
      }
    })();

    return this.refreshInFlight;
  }
}

export function memoryTokenStore(initial: TokenPair | null = null): TokenStore {
  let tokens = initial;
  return {
    read: () => tokens,
    write: (next) => {
      tokens = next;
    },
  };
}

export function localStorageTokenStore(key = '4zen.tokens'): TokenStore {
  return {
    read() {
      try {
        const raw = localStorage.getItem(key);
        return raw ? (JSON.parse(raw) as TokenPair) : null;
      } catch {
        return null;
      }
    },
    write(tokens) {
      if (tokens) localStorage.setItem(key, JSON.stringify(tokens));
      else localStorage.removeItem(key);
    },
  };
}
